// Generic analytics utility for tracking events

export interface EventData {
  [key: string]: any;
}

/**
 * Tracks an event with the specified event type and data
 * @param eventType The type of event to track
 * @param data Additional data to include with the event
 * @returns A promise that resolves to a result object
 */
export async function trackEvent(
  eventType: string,
  data: EventData = {}
): Promise<{ success: boolean; error?: string }> {
  try {
    // Replace this URL with your actual analytics endpoint
    const analyticsEndpoint = process.env['REACT_APP_ANALYTICS_ENDPOINT'] ||
      "https://api.somewords.app/analytics/track-event";
    
    const response = await fetch(analyticsEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ 
        eventType, 
        data: {
          ...data,
          timestamp: new Date().toISOString(),
          // You can add additional default properties here
          // userAgent: navigator.userAgent,
          // language: navigator.language,
        }
      }),
    });

    if (response.ok) {
      return { success: true };
    } else {
      return {
        success: false,
        error: `Failed to record event: ${response.statusText}`,
      };
    }
  } catch (error) {
    console.error(`Error tracking event ${eventType}:`, error);
    return { success: false, error: "Network error occurred" };
  }
}

/**
 * Tracks a page view
 * @param pageName The name of the page being viewed
 * @param additionalData Additional data to include with the event
 */
export function trackPageView(pageName: string, additionalData: EventData = {}) {
  return trackEvent("page_view", { 
    page: pageName,
    path: window.location.pathname,
    ...additionalData
  });
}

/**
 * Tracks a button click
 * @param buttonId The ID or name of the button
 * @param additionalData Additional data to include with the event
 */
export function trackButtonClick(buttonId: string, additionalData: EventData = {}) {
  return trackEvent("button_click", {
    buttonId,
    ...additionalData
  });
}

/**
 * Tracks an app store click
 * @param storeType The type of app store (e.g., "app_store", "play_store")
 * @param source The source of the click (e.g., "hero_section", "footer")
 */
export function trackStoreClick(
  storeType: "app_store" | "play_store", 
  source: string
) {
  return trackEvent(`${storeType}_click`, { source });
}